<template>
  <div class="imei-input-modal">
    <div class="imei-input-modal__trigger">
      <span class="text-primary imei-input-modal__link" @click.stop="openModal">
        <i class="fas fa-list-ul text-primary"></i>
        {{ triggerLabel }}
      </span>
    </div>

    <b-modal
      ref="imeiModal"
      size="xl"
      hide-footer
      :id="modalId"
      @hidden="onModalHidden"
    >
      <template #modal-title>
        <div class="imei-input-modal__header">
          <h5 class="mb-1 font-weight-bolder">
            IMEI cho sản phẩm {{ productItem.productName }}
          </h5>
          <div class="imei-input-modal__meta text-muted">
            Mã SP: {{ productItem.productCode || '—' }}
            · Barcode {{ productItem.barCode || '—' }}
          </div>
        </div>
      </template>

      <div v-if="showTabs" class="imei-input-modal__tabs mb-3">
        <button
          type="button"
          class="imei-input-modal__tab"
          :class="{ 'imei-input-modal__tab--active': activeTab === 'stock' }"
          @click="switchTab('stock')"
        >
          Chọn từ tồn kho ({{ stockTotal }})
        </button>
        <button
          type="button"
          class="imei-input-modal__tab"
          :class="{ 'imei-input-modal__tab--active': activeTab === 'manual' }"
          @click="switchTab('manual')"
        >
          Nhập tay ({{ manualTotal }})
        </button>
      </div>

      <ImeiInputViewTab
        v-if="modalMode === 'view'"
        :items="filteredViewImeis"
        :search-term.sync="viewSearch"
        :current-page.sync="viewPage"
        :per-page="perPage"
        :total-count="viewTotal"
        :filtered-count="viewFilteredCount"
        :export-disabled="!viewTotal"
        @export="onExportView"
      />

      <ImeiInputStockTab
        v-if="showStockTab"
        :items="products"
        :search-term.sync="stockSearch"
        :loading="isLoadingList"
        :current-page.sync="stockPage"
        :per-page="perPage"
        :total-count="stockTotal"
        :filtered-count="stockFilteredCount"
        :selected-count="stockSelectedCount"
        :export-disabled="!products.length"
        @search="onStockSearch"
        @template="onDownloadTemplate"
        @import="onImportExcel"
        @export="onExportStock"
        @toggle-selected="onToggleStockSelected"
      />

      <ImeiInputManualTab
        v-if="showManualTab"
        :items="filteredManualImeis"
        :new-imei-input.sync="newImeiInput"
        :search-term.sync="manualSearch"
        :current-page.sync="manualPage"
        :per-page="perPage"
        :total-count="manualTotal"
        :filtered-count="manualFilteredCount"
        :export-disabled="!inputImeis.length"
        @add="onAddManualImei"
        @remove="onRemoveManualImei"
        @template="onDownloadTemplate"
        @import="onImportExcel"
        @export="onExportManual"
      />

      <div class="imei-input-modal__footer d-flex flex-wrap align-items-center justify-content-between mt-3">
        <span class="imei-input-modal__summary text-muted">{{ footerSummary }}</span>
        <div class="imei-input-modal__actions">
          <template v-if="modalMode === 'view'">
            <b-button variant="secondary" size="sm" class="mr-2" @click="hideModal">
              <i class="fas fa-times"></i>
              <strong>Đóng</strong>
            </b-button>
            <b-button
              v-if="!readonly"
              variant="primary"
              size="sm"
              @click="switchToEdit"
            >
              <i class="fas fa-edit"></i>
              <strong>Chỉnh sửa</strong>
            </b-button>
          </template>
          <template v-else>
            <b-button variant="secondary" size="sm" class="mr-2" @click="hideModal">
              <i class="fas fa-times"></i>
              <strong>Hủy</strong>
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              :disabled="!canApply"
              @click="onApply"
            >
              <i class="fas fa-check"></i>
              <strong>Áp dụng</strong>
            </b-button>
          </template>
        </div>
      </div>
    </b-modal>

    <ImportImei ref="importImeiModal" @saveData="onSaveDataImportImei" />
  </div>
</template>

<script>
  import ApiService from '@/core/services/api.service'
  import { makeToastFaile, makeToastSuccess } from '@/utils/common'
  import { TIME_TRIGGER } from '@/utils/constants'
  import { parseImeiCodes } from '@/view/components/bills/helpers/wholesaleImeiBillTransform'
  import ImportImei from './import-excels/ImportImei.vue'
  import ImeiInputStockTab from './ImeiInputStockTab.vue'
  import ImeiInputManualTab from './ImeiInputManualTab.vue'
  import ImeiInputViewTab from './ImeiInputViewTab.vue'
  import {
    applyChooseImeis,
    applyManualImeis,
    appendMissingSelectedImeisToList,
    countSelected,
    dedupeManualImeis,
    filterImeiRows,
    filterStockImeis,
    normalizeImeiCode,
    parseManualImeisFromString,
    setStockImeiSelected,
    validateImeiCode,
  } from './imeiInputForm.logic'
  import {
    buildImeiExportFilename,
    downloadImeiTemplate,
    exportImeisToExcel,
  } from './imeiInputFormExcel'

  export default {
    name: 'ImeiInputModal',
    components: {
      ImportImei,
      ImeiInputStockTab,
      ImeiInputManualTab,
      ImeiInputViewTab,
    },
    props: {
      productItem: {
        type: Object,
        required: true,
      },
      storeId: {
        type: Number,
      },
      inStock: {
        type: Boolean,
        default: false,
      },
      quantityKey: {
        type: String,
        default: 'quantity',
      },
      statusViews: {
        type: Array,
        default: () => [1],
      },
      enableViewEditMode: {
        type: Boolean,
        default: false,
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      stockOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      const uniqueId =
        this.productItem.productId ||
        `imei-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      return {
        modalId: `modal-imei-unified-${uniqueId}`,
        modalMode: 'edit',
        activeTab: this.inStock ? 'stock' : 'manual',
        products: [],
        productsTerm: [],
        inputImeis: [],
        stockSearch: '',
        manualSearch: '',
        viewSearch: '',
        newImeiInput: '',
        stockPage: 1,
        manualPage: 1,
        viewPage: 1,
        perPage: 50,
        isLoadingList: false,
        importFromStockTab: false,
      }
    },
    computed: {
      imeiCount() {
        const raw = (this.productItem.IMEI || this.productItem.imeiCode || '').trim()
        return raw ? parseImeiCodes(raw).length : 0
      },
      triggerLabel() {
        return this.imeiCount > 0 ? `IMEI (${this.imeiCount})` : 'Quản lý IMEI'
      },
      showTabs() {
        return this.inStock && !this.stockOnly && this.modalMode === 'edit'
      },
      showStockTab() {
        return (
          this.modalMode === 'edit' &&
          this.inStock &&
          (this.stockOnly || this.activeTab === 'stock')
        )
      },
      showManualTab() {
        return (
          this.modalMode === 'edit' &&
          !this.stockOnly &&
          (!this.inStock || this.activeTab === 'manual')
        )
      },
      viewImeiRows() {
        const raw = (this.productItem.IMEI || this.productItem.imeiCode || '').trim()
        if (!raw) return []
        return parseImeiCodes(raw).map((code) => ({ imeiCode: code }))
      },
      filteredViewImeis() {
        return filterImeiRows(this.viewImeiRows, this.viewSearch)
      },
      viewTotal() {
        return this.viewImeiRows.length
      },
      viewFilteredCount() {
        return this.filteredViewImeis.length
      },
      stockTotal() {
        return this.productsTerm.length
      },
      stockFilteredCount() {
        return this.products.length
      },
      stockSelectedCount() {
        return countSelected(this.productsTerm)
      },
      manualTotal() {
        return this.inputImeis.length
      },
      filteredManualImeis() {
        return filterImeiRows(this.inputImeis, this.manualSearch)
      },
      manualFilteredCount() {
        return this.filteredManualImeis.length
      },
      isStockEditMode() {
        return this.stockOnly || (this.inStock && this.activeTab === 'stock')
      },
      footerSummary() {
        if (this.modalMode === 'view') {
          const n = this.viewTotal
          return n ? `Đang xem ${n} IMEI` : 'Chưa có IMEI nào'
        }
        if (this.isStockEditMode) {
          const n = this.stockSelectedCount
          return n ? `Sẽ áp dụng ${n} IMEI đã chọn` : 'Chưa chọn IMEI nào'
        }
        const n = this.manualTotal
        return n ? `Sẽ áp dụng ${n} IMEI` : 'Chưa có IMEI nào'
      },
      canApply() {
        if (this.isStockEditMode) {
          return this.stockSelectedCount > 0
        }
        return this.manualTotal > 0
      },
    },
    watch: {
      manualSearch() {
        this.manualPage = 1
      },
      viewSearch() {
        this.viewPage = 1
      },
    },
    created() {
      this.syncQuantityFromImei()
    },
    methods: {
      syncQuantityFromImei() {
        const fromCode = (this.productItem.imeiCode || '').trim()
        const fromImei = (this.productItem.IMEI || '').trim()
        if (!fromImei && fromCode) {
          this.$set(this.productItem, 'IMEI', fromCode)
        }
        const imeiString = (this.productItem.IMEI || this.productItem.imeiCode || '').trim()
        const n = imeiString ? parseImeiCodes(imeiString).length : 0
        this.productItem[this.quantityKey] = n
      },
      openModal() {
        this.hydrateFromProductItem()
        this.activeTab = this.inStock ? 'stock' : 'manual'
        this.stockSearch = ''
        this.manualSearch = ''
        this.viewSearch = ''
        this.newImeiInput = ''
        this.stockPage = 1
        this.manualPage = 1
        this.viewPage = 1

        if (this.enableViewEditMode && this.imeiCount > 0) {
          this.modalMode = 'view'
        } else {
          this.modalMode = 'edit'
          if (this.inStock) {
            this.loadStockImeis()
          }
        }

        this.$refs.imeiModal.show()
      },
      switchToEdit() {
        this.modalMode = 'edit'
        this.activeTab = 'stock'
        this.stockSearch = ''
        this.viewSearch = ''
        this.stockPage = 1
        if (this.inStock) {
          this.loadStockImeis()
        }
      },
      hideModal() {
        this.$refs.imeiModal.hide()
      },
      onModalHidden() {
        this.stockSearch = ''
        this.manualSearch = ''
        this.viewSearch = ''
        this.modalMode = 'edit'
      },
      switchTab(tab) {
        this.activeTab = tab
        if (tab === 'stock' && !this.productsTerm.length && this.inStock) {
          this.loadStockImeis()
        }
      },
      hydrateFromProductItem() {
        const imeiString = (this.productItem.IMEI || '').trim()
        this.inputImeis = parseManualImeisFromString(imeiString)
      },
      loadStockImeis() {
        if (!this.storeId || !this.productItem.productId) {
          this.products = []
          this.productsTerm = []
          return
        }
        this.isLoadingList = true
        const params = {
          productId: this.productItem.productId,
          storeId: this.storeId,
          imeiStatus: this.statusViews,
        }
        const imeiRaw = (this.productItem.IMEI || this.productItem.imeiCode || '').trim()
        const currentImeis = imeiRaw ? parseImeiCodes(imeiRaw) : []
        const product = this.productItem

        ApiService.query('imeis/get-by-product', { params })
          .then((response) => {
            const { data } = response.data
            const products = []
            ;(data || []).forEach((element) => {
              const isSelected = currentImeis.includes(element.imeiNo)
              products.push({
                ...element,
                productName: product.productName,
                imeiCode: element.imeiNo,
                selected: isSelected,
              })
            })
            appendMissingSelectedImeisToList(products, product, currentImeis)
            setTimeout(() => {
              this.isLoadingList = false
            }, TIME_TRIGGER)
            this.productsTerm = products
            this.products = this.productsTerm
          })
          .catch(() => {
            const products = []
            appendMissingSelectedImeisToList(products, product, currentImeis)
            this.productsTerm = products
            this.products = this.productsTerm
            this.isLoadingList = false
          })
      },
      onToggleStockSelected({ item, selected }) {
        setStockImeiSelected(this.productsTerm, item, selected)
      },
      onStockSearch() {
        const { filtered, hasExactMatch } = filterStockImeis(
          this.productsTerm,
          this.stockSearch,
        )
        if (hasExactMatch) {
          this.stockSearch = ''
          this.stockPage = 1
          this.products = this.productsTerm
          return
        }
        if (filtered.length <= this.perPage) {
          this.stockPage = 1
        }
        this.products = filtered
      },
      onAddManualImei() {
        const imeiCode = normalizeImeiCode(this.newImeiInput)
        if (!imeiCode) return

        const validation = validateImeiCode(imeiCode)
        if (!validation.valid) {
          return makeToastFaile(validation.message)
        }
        const exists = this.inputImeis.some(
          (item) => normalizeImeiCode(item.imeiCode) === imeiCode,
        )
        if (exists) {
          return makeToastFaile(`Mã IMEI ${imeiCode} đã tồn tại trong danh sách`)
        }
        this.inputImeis.push({ imeiCode, selected: true })
        this.newImeiInput = ''
      },
      onRemoveManualImei(imeiCode) {
        const needle = normalizeImeiCode(imeiCode)
        const index = this.inputImeis.findIndex(
          (item) => normalizeImeiCode(item.imeiCode) === needle,
        )
        if (index < 0) {
          return makeToastFaile(`Không tìm thấy mã IMEI ${imeiCode} trong danh sách`)
        }
        this.inputImeis.splice(index, 1)
      },
      onApply() {
        if (this.isStockEditMode) {
          const { imeiString, quantity } = applyChooseImeis(this.productsTerm)
          this.productItem.IMEI = imeiString
          this.productItem[this.quantityKey] = quantity
          if (this.productItem.imeiCode !== undefined) {
            this.productItem.imeiCode = imeiString
          }
        } else {
          const { imeiString, quantity } = applyManualImeis(this.inputImeis)
          this.productItem.IMEI = imeiString
          this.productItem[this.quantityKey] = quantity
          if (this.productItem.imeiCode !== undefined) {
            this.productItem.imeiCode = imeiString
          }
        }
        this.hideModal()
      },
      onDownloadTemplate() {
        downloadImeiTemplate('Mau_IMEI')
      },
      onImportExcel() {
        this.importFromStockTab = this.isStockEditMode
        this.$refs.importImeiModal.showModal()
      },
      onExportStock() {
        const rows = this.products.length ? this.products : this.productsTerm
        if (!rows.length) {
          return makeToastFaile('Không có dữ liệu IMEI để xuất')
        }
        const filename = buildImeiExportFilename(this.productItem.productCode)
        const ok = exportImeisToExcel(rows, `${filename}.xlsx`, { includeSelected: true })
        if (!ok) makeToastFaile('Không có dữ liệu IMEI để xuất')
      },
      onExportManual() {
        const rows = this.filteredManualImeis.length
          ? this.filteredManualImeis
          : this.inputImeis
        if (!rows.length) {
          return makeToastFaile('Không có dữ liệu IMEI để xuất')
        }
        const filename = buildImeiExportFilename(this.productItem.productCode)
        const ok = exportImeisToExcel(rows, `${filename}.xlsx`)
        if (!ok) makeToastFaile('Không có dữ liệu IMEI để xuất')
      },
      onExportView() {
        const rows = this.filteredViewImeis.length
          ? this.filteredViewImeis
          : this.viewImeiRows
        if (!rows.length) {
          return makeToastFaile('Không có dữ liệu IMEI để xuất')
        }
        const filename = buildImeiExportFilename(this.productItem.productCode)
        const ok = exportImeisToExcel(rows, `${filename}.xlsx`)
        if (!ok) makeToastFaile('Không có dữ liệu IMEI để xuất')
      },
      applyImportedImeisToChooseList(imeiCodes) {
        const imported = (imeiCodes || [])
          .map((row) => normalizeImeiCode(row?.imeiCode))
          .filter(Boolean)
        if (!imported.length) {
          return makeToastFaile('Vui lòng nhập dữ liệu IMEI hợp lệ từ file excel')
        }
        const importedSet = new Set(imported)
        let matched = 0
        this.productsTerm.forEach((row) => {
          const code = normalizeImeiCode(row.imeiCode)
          if (importedSet.has(code)) {
            row.selected = true
            matched++
          }
        })
        if (matched === 0) {
          return makeToastFaile(
            'Không tìm thấy IMEI hợp lệ trong danh sách tồn kho của sản phẩm',
          )
        }
        this.stockSearch = ''
        this.onStockSearch()
        makeToastSuccess(`Đã chọn ${matched}/${importedSet.size} IMEI khớp từ file excel`)
      },
      onSaveDataImportImei(imeiCodes) {
        if (this.importFromStockTab) {
          this.importFromStockTab = false
          this.applyImportedImeisToChooseList(imeiCodes)
          return
        }
        this.inputImeis = dedupeManualImeis(this.inputImeis, imeiCodes)
      },
    },
  }
</script>

<style scoped>
  .imei-input-modal__trigger {
    display: inline-block;
    transform: translateY(10px);
    margin-left: 4px;
    font-size: 12px;
    max-width: 100%;
  }
  .imei-input-modal__link {
    cursor: pointer;
    white-space: nowrap;
    font-size: 12px;
  }
  .imei-input-modal__header h5 {
    color: #181c32;
    font-size: 1.05rem;
  }
  .imei-input-modal__meta {
    font-size: 12px;
  }
  .imei-input-modal__tabs {
    display: inline-flex;
    background: #eef0f4;
    border-radius: 8px;
    padding: 4px;
    gap: 4px;
  }
  .imei-input-modal__tab {
    border: none;
    background: transparent;
    color: #7e8299;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 14px;
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
  }
  .imei-input-modal__tab--active {
    background: #fff;
    color: #181c32;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .imei-input-modal__summary {
    font-size: 13px;
    font-weight: 500;
  }
  .imei-input-modal__actions .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
  }
</style>
